import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { useSelector } from "react-redux";
import { routes } from "../../../../../constants/routes";
import { getProviderRequestDetails } from "../../../../../api/admin/provider-management";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import "./view-provider-request-details.scss";
import { AlertComponent } from "../../../../../common/components";
import RequestStatus from "../../../../../common/components/request-status/request-status";
import cancelIcon from "../../../../../assets/icons/cancel-icon.svg";
import tickIcon from "../../../../../assets/icons/tickIcon.svg";
import {
  approveProviderRequest,
  rejectProviderRequest,
} from "../../../../../api/admin/provider-management";
import ApproveOrRejectPopup from "./approve-or-reject-popup";
import { useNavigate, useLocation } from "react-router-dom";
import ContactLink from "../../../../../common/components/contact-link/contact-link";

const ViewProviderRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string>("-");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [isApprovePopupOpen, setIsApprovePopupOpen] = useState<boolean>(false);
  const [isRejectPopupOpen, setIsRejectPopupOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPlannerSection = location.pathname.includes("planner-management");

  const breadcrumbLink = isPlannerSection
    ? [
        {
          label: "Planner Management",
          link: routes.PLANNER_REQUESTS,
          stateData: { ...location.state },
        },
        {
          label: "Registration Requests",
          link: routes.PLANNER_REQUESTS,
          stateData: { ...location.state },
        },
        { label: "Planner Request Details", link: "#" },
      ]
    : [
        {
          label: "Provider Management",
          link: routes.PROVIDER_REQUESTS,
          stateData: { ...location.state },
        },
        {
          label: "Provider Requests",
          link: routes.PROVIDER_REQUESTS,
          stateData: { ...location.state },
        },
        { label: "Provider Details", link: "#" },
      ];

  const fetchRequestDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getProviderRequestDetails(id);
      if (response?.status === 200 || response?.data?.success) {
        setData(response?.data?.data || {});
      } else {
        setError("Failed to fetch request details.");
      }
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message || "An error occurred while fetching data."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  const handleApproveRequestAPI = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await approveProviderRequest(id);
      if (response?.status === 200 || response?.data?.success) {
        setTimeout(() => {
          navigate(isPlannerSection ? routes.PLANNER_REQUESTS : routes.PROVIDER_REQUESTS, {
            state: {
              alertMessage: isPlannerSection
                ? "Planner request approved successfully."
                : "Provider request approved successfully.",
              alertType: "success",
              fromView: true,
              searchText: location.state?.searchText || "",
              activeFilters: location.state?.activeFilters || {}
            },
          });
        }, 100);
      } else {
        setAlertMessage(
          isPlannerSection ? "Failed to approve planner request." : "Failed to approve provider request."
        );
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message ||
        "An error occurred while approving request."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequestAPI = async (reason: string) => {
    if (!id) return;
    setLoading(true);
    if (!reason || reason.trim() === "") {
      setAlertMessage("Rejection reason is required.");
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
      return;
    }
    try {
      const response = await rejectProviderRequest(id, { reason });
      if (response?.status === 200 || response?.data?.success) {
        setTimeout(() => {
          navigate(isPlannerSection ? routes.PLANNER_REQUESTS : routes.PROVIDER_REQUESTS, {
            state: {
              alertMessage: isPlannerSection
                ? "Planner request rejected successfully."
                : "Provider request rejected successfully.",
              alertType: "success",
              fromView: true,
              searchText: location.state?.searchText || "",
            },
          });
        }, 100);
      } else {
        setAlertMessage(
          isPlannerSection ? "Failed to reject planner request." : "Failed to reject provider request."
        );
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message ||
        "An error occurred while rejecting request."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = () => { };

  const handleRejectRequest = () => { };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const statusClass = data?.status?.toLowerCase() || "pending";

  const handleBackArrowClick = () => {
    navigate(routes.PROVIDER_REQUESTS, { state: { searchText: location.state?.searchText || "", activeFilters: location.state?.activeFilters || {} } });
  }

  const getFilePreviewURL = (item: any) => {
    const fileName = item?.fileName || "";
    const fileExtension = fileName.split(".").pop().toLowerCase();
    const microsoftOfficeExtensions = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
    if (microsoftOfficeExtensions.includes(fileExtension)) {
      return `https://docs.google.com/gview?url=${encodeURIComponent(
        item?.url
      )}`;
    }
    return item?.url || "#";
  }

  const fetchNeedTypeLabel = (needTypes: any[]) => {
    if (needTypes.length === 0) return "-";
    else if (needTypes.length === 2) return "At-Need, Preneed";
    else if (needTypes[0] === "at_need") return "At-Need";
    else if (needTypes[0] === "pre_need") return "Preneed";
    else return "-";
  }

  return (
    <div className="view-provider-request-details-main-wrapper">
      <ProfileHeader
        breadcrumbLink={breadcrumbLink}
        showBackArrow={true}
        handleBackArrowClick={handleBackArrowClick}
      />
      <div className="details-page-inner">
        {data && (
          <div className="provider-card">
            {/* Header */}
            <div className="card-header">
              <div className="name-and-status-row">
                <h2 title={data?.businessInformation?.businessName || "-"}>
                  {data?.businessInformation?.businessName || "-"}
                </h2>
                <RequestStatus
                  status={
                    data?.status
                      ? data.status.charAt(0).toUpperCase() +
                      data.status.slice(1)
                      : "-"
                  }
                />
              </div>
              <div className="request-id">
                Request ID: {data?.requestId || "-"}
              </div>
            </div>

            <div className="separator"></div>

            {/* Business Information Section */}
            <p className="section-title">Business Information</p>

            <div className="info-grid">
              <div className="info-item">
                <label>Business Name</label>
                <p className="field-value">
                  {data?.businessInformation?.businessName || "-"}
                </p>
              </div>

              <div className="info-item">
                <label>Business Type</label>
                <p className="field-value">
                  {data?.businessInformation?.businessType || "-"}
                </p>
              </div>

              <div className="info-item">
                <label>Years in Operation</label>
                <p className="field-value">
                  {data?.businessDetails?.yearsInOperation || 0} Years
                </p>
              </div>

              <div className="info-item">
                <label>Business Address</label>
                <p className="field-value">
                  {data?.businessInformation?.businessAddress || "-"}
                </p>
              </div>

              <div className="info-item">
                <label>City</label>
                <p className="field-value">
                  {data?.businessInformation?.address?.city || "-"}
                </p>
              </div>

              <div className="info-item">
                <label>State</label>
                <p className="field-value">
                  {data?.businessInformation?.address?.state || "-"}
                </p>
              </div>

              <div className="info-item">
                <label>Zip Code</label>
                <p className="field-value">
                  {data?.businessInformation?.address?.zipcode || "-"}
                </p>
              </div>

              <div className="info-item">
                <label>Country</label>
                <p className="field-value">
                  {data?.businessInformation?.address?.country || "-"}
                </p>
              </div>

              <div className="info-item">
                <label>Business Phone</label>
                <ContactLink
                  type="Phone"
                  value={data?.businessInformation?.businessPhone || "-"}
                  className="field-value link-style"
                />
              </div>

              <div className="info-item">
                <label>Business Email</label>
                <ContactLink
                  type="Email"
                  value={data?.businessInformation?.businessEmail || "-"}
                  className="field-value link-style"
                />
              </div>

              <div className="info-item">
                <label>Business Website</label>
                <a
                  href={data?.businessInformation?.businessWebsite || "#"}
                  target="_blank"
                  rel="noreferrer"
                >
                  {data?.businessInformation?.businessWebsite || "-"}
                </a>
              </div>
              <div className="info-item">
                <label>Business Description</label>
                <p className="field-value">
                  {data?.businessDetails?.businessDescription || "-"}
                </p>
              </div>
              <div className="info-item">
                <label>Emergency / 24×7 Service Availability</label>
                <div className="field-value emergency-service-availability">
                  {data?.businessDetails?.isEmergencyServiceAvailable ? (
                    <>
                      Yes
                      <div className="nested-field-value">
                        {" / Contact: "}&nbsp;
                        <ContactLink
                          type="Phone"
                          value={
                            data?.businessDetails
                              ?.emergencyContactPhone || "-"
                          }
                          className="link-style"
                        />
                      </div>
                    </>
                  ) : (
                    "No"
                  )}
                </div>
              </div>
            </div>
            <div className="separator"></div>
            {/* Primary Information Section */}
            <p className="section-title">Primary Information</p>
            <div className="info-grid">
              <div className="info-item">
                <label>Primary Contact Name</label>
                <p className="field-value">
                  {data?.primaryInformation?.contactName || "-"}
                </p>
              </div>
              <div className="info-item">
                <label>Contact Role / Designation</label>
                <p className="field-value">
                  {data?.primaryInformation?.contactRole || "-"}
                </p>
              </div>
              <div className="info-item">
                <label>Primary Contact Phone</label>
                <ContactLink
                  type="Phone"
                  value={data?.primaryInformation?.contactPhone || "-"}
                  className="field-value link-style"
                />
              </div>
              <div className="info-item">
                <label>Primary Contact Email</label>
                <ContactLink
                  type="Email"
                  value={data?.primaryInformation?.contactEmail || "-"}
                  className="field-value link-style"
                />
              </div>
            </div>

            <div className="separator"></div>
            {/* Approve Reject Buttons as footer */}
            <div className=" approve-reject-buttons-footer">
              <button
                className="reject-button"
                disabled={!data?.actions?.canReject}
                onClick={() => setIsRejectPopupOpen(true)}
              >
                <img src={cancelIcon} alt="Reject" className="cancel-icon" />
                Reject Request
              </button>
              <button
                className="approve-button"
                disabled={!data?.actions?.canApprove}
                onClick={() => setIsApprovePopupOpen(true)}
              >
                <img src={tickIcon} alt="Approve" className="approve-icon" />
                Approve Request
              </button>
            </div>
          </div>
        )}
        <ApproveOrRejectPopup
          open={isApprovePopupOpen}
          title={
            data?.businessInformation?.businessName ||
            (isPlannerSection ? "Approve Planner Request" : "Approve Provider Request")
          }
          note={
            isPlannerSection
              ? "This planner will be granted access to the platform and can start offering services."
              : "This provider will be granted access to the platform and can start offering services."
          }
          buttonText="Approve"
          onOkay={() => {
            setIsApprovePopupOpen(false);
            handleApproveRequestAPI();
          }}
          onCancel={() => setIsApprovePopupOpen(false)}
          fieldName="approve"
        />
        <ApproveOrRejectPopup
          open={isRejectPopupOpen}
          title={
            data?.businessInformation?.businessName ||
            (isPlannerSection ? "Reject Planner Request" : "Reject Provider Request")
          }
          note={
            isPlannerSection
              ? "This planner will be notified that their request has been rejected. This action cannot be undone."
              : "This provider will be notified that their request has been rejected. This action cannot be undone."
          }
          buttonText="Reject"
          isInputRequired={true}
          onReject={(reason: string) => {
            setIsRejectPopupOpen(false);
            handleRejectRequestAPI(reason);
          }}
          onCancel={() => setIsRejectPopupOpen(false)}
          fieldName="reject"
        />
      </div>
      <AlertComponent
        open={alertOpen}
        message={alertMessage || ""}
        type={alertType}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default ViewProviderRequestDetails;
